import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import ImageComponent from "./ImageComponent";
import Api from "../../../Api/api";
import { toast } from "react-toastify";
import { useStateValue } from "../../../states/StateProvider";

const Services = ({data, handleClick, setEditStates, FPID}) => {
    const [{ upsellingSaved }, dispatch] = useStateValue();
    const [showProzessDropdown, setShowProzessDropdown] = useState(false);
    const [currentProzessValue, setCurrentProzessValue] = useState(null);
    const [loading, setLoading] = useState(false);
    const triggerRef = useRef(null);
    const [menuStyle, setMenuStyle] = useState({});
    const [prozessOptions, setProzessOptions] = useState([]);

    // Fetch Prozess options from API
    useEffect(() => {
        const fetchProzessOptions = async () => {
            try {
                const response = await Api().post('/sp_getProcessOptions');
                // Map the API response format {optID, optValue} to {ID, title} for compatibility
                const mappedOptions = response.data.map(opt => ({
                    ID: opt.optID,
                    title: opt.optValue
                }));
                setProzessOptions(mappedOptions);
            } catch (error) {
                console.error('Fehler beim Laden der Prozess-Optionen:', error);
                toast.error('Fehler beim Laden der Prozess-Optionen');

                // Fallback options
                const fallbackOptions = [
                    { title: "Closing", ID: 1 },
                    { title: "Upselling", ID: 2 },
                    { title: "Cross-Selling", ID: 3 },
                ];

                setProzessOptions(fallbackOptions);
            }
        };
        fetchProzessOptions();
    }, []);

    // Initialize currentProzessValue from data when data changes
    useEffect(() => {
        if (data && data.Prozess) {
            const displayText = data.Prozess.split(",")[0];
            setCurrentProzessValue(displayText);
        }
    }, [data]);

    // Position dropdown
    const positionDropdown = useCallback(() => {
        const el = triggerRef.current;
        if (!el) return;

        const rect = el.getBoundingClientRect();
        const padding = 8;
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        const belowSpace = vh - rect.bottom - padding;
        const aboveSpace = rect.top - padding;
        const useBelow = belowSpace >= 180 || belowSpace >= aboveSpace;

        const left = Math.min(rect.left, vw - rect.width - padding);
        const width = rect.width;
        const maxHeight = useBelow ? belowSpace : aboveSpace;
        const top = useBelow ? rect.bottom + 4 : Math.max(padding, rect.top - Math.min(300, maxHeight) - 4);

        setMenuStyle({
            position: "fixed",
            top,
            left,
            width,
            maxHeight: Math.max(140, maxHeight),
            overflowY: "auto",
            zIndex: 60,
        });
    }, []);

    useEffect(() => {
        const onDoc = () => setShowProzessDropdown(false);
        if (showProzessDropdown) {
            positionDropdown();
            window.addEventListener("resize", positionDropdown);
            window.addEventListener("scroll", positionDropdown, true);
            document.addEventListener("mousedown", onDoc);
        }
        return () => {
            window.removeEventListener("resize", positionDropdown);
            window.removeEventListener("scroll", positionDropdown, true);
            document.removeEventListener("mousedown", onDoc);
        };
    }, [showProzessDropdown, positionDropdown]);

    const handleProzessSelect = async (option) => {
        setShowProzessDropdown(false);
        setLoading(true);

        const payload = {
            UID: parseInt(option.ID, 10), // Ensure UID is an integer
            portal: "dgg",
            ID: 1, // editID for Prozess
            FP_ID: parseInt(FPID, 10), // Ensure FP_ID is an integer
            status: option.title
        };

        console.log('Sending Process option to backend:', payload); // Debug log

        try {
            await Api().post("/sp_setUpselling", payload);
            toast.success("Prozess erfolgreich aktualisiert");
            // Update local state to immediately reflect the change
            setCurrentProzessValue(option.title);
            dispatch({ type: "SET_UPSELLING_SAVED", item: !upsellingSaved });
        } catch (error) {
            console.error('Error saving Process option:', error);
            toast.error("Etwas ist schief gelaufen!!");
        } finally {
            setLoading(false);
        }
    };

    return (<div className="my-3 w-full rounded-lg bg-white 2xl:w-5/12 sm:block">
            <div className="p-3 text-left text-2xl font-bold">Dienstleistungen</div>

            <table className="bg-white text-left">
                <thead className="sticky top-0 whitespace-nowrap bg-white">
                <tr>
                    <th className="tooltip pl-1.5 text-sm text-grey"></th>
                    <th className="tooltip pl-1.5 text-sm text-grey"></th>
                    <th className="tooltip pl-1.5 text-sm text-grey"></th>
                    <th className="tooltip pl-1.5 text-sm text-grey"></th>
                </tr>
                </thead>

                <tbody className="gap-4">
                {data && Object.entries(data)?.slice(1)?.length > 0 ? (Object.entries(data)
                        ?.slice(1)
                        ?.filter(([key]) => !key.includes("Anzahl"))
                        .map((u, index) => {
                            const imageSrc = `${window.location.origin}/icons/${u[1]
                                .split(",")[0]
                                .replace(/\s/g, "")}.png`;
                            const displayText = u[1].split(",")[0];
                            const formattedDate = u[1].split(",")[1]?.split(":")[1];

                            const trClass = u[0] === "Upselling" ? "text-sm border border-b-1 border-b-offWhite border-x-0 border-t-0" : "text-sm";

                            const locked = displayText === "Vertrag aktiv" || displayText === "Vertrag inaktiv";
                            const isProzess = u[0] === "Prozess";

                            return (<tr key={u[0]} className={trClass}>
                                    <td>{u[0]}</td>

                                    <td className="mt-2 flex gap-4 border border-offWhite p-2">
                                        {isProzess ? (
                                            <div className="relative w-full">
                                                <div
                                                    ref={triggerRef}
                                                    className={`w-full cursor-pointer rounded-md border border-whiteDark bg-white px-4 py-2 text-left flex items-center justify-between ${
                                                        loading ? "cursor-not-allowed opacity-70" : "hover:border-mainBlue"
                                                    }`}
                                                    onClick={() => !loading && setShowProzessDropdown((s) => !s)}
                                                >
                                                    <span>{loading ? "speichere..." : (currentProzessValue || displayText)}</span>
                                                    <span className="text-gray-400">{showProzessDropdown ? "▲" : "▼"}</span>
                                                </div>

                                                {showProzessDropdown &&
                                                    createPortal(
                                                        <div
                                                            style={menuStyle}
                                                            className="rounded-md border border-whiteDark bg-white shadow-md"
                                                            onMouseDown={(e) => e.stopPropagation()}
                                                        >
                                                            {prozessOptions.map((op) => (
                                                                <div
                                                                    key={op.ID}
                                                                    className="cursor-pointer text-text hover:bg-gray-50 flex items-center gap-2 px-4 py-2"
                                                                    onClick={() => handleProzessSelect(op)}
                                                                >
                                                                    <span>{op.title}</span>
                                                                </div>
                                                            ))}
                                                        </div>,
                                                        document.body
                                                    )}
                                            </div>
                                        ) : (
                                            <>
                                                {displayText !== "Keine Information" && (<ImageComponent src={imageSrc}/>)}
                                                <div className="flex w-60 justify-between">
                                                    <span>{displayText}</span>
                                                    <span>{formattedDate}</span>
                                                </div>
                                            </>
                                        )}
                                    </td>

                                    <td>
                                        {!isProzess && (
                                            <button
                                                className={`rounded-lg py-2 px-4 text-center text-offWhite ${locked ? "cursor-not-allowed bg-lightBlue" : "bg-mainBlue"}`}
                                                disabled={locked}
                                                onClick={() => setEditStates(u, index)}
                                            >
                                                bearbeiten
                                            </button>
                                        )}
                                    </td>
                                </tr>);
                        })) : (<tr>
                        <td colSpan="3">
                            Bitte die Upselling-Bausteine unter{" "}
                            <a
                                style={{color: "#3A46A9", textDecoration: "underline"}}
                                href="#"
                                onClick={handleClick}
                            >
                                Portal@helmsauer-gruppe.de
                            </a>{" "}
                            freischalten lassen.
                        </td>
                    </tr>)}
                </tbody>
            </table>
        </div>);
};

export default Services;
